import { useState } from "react"
import { OvertimeRequest } from "@/types"
import { formatDate, formatHours } from "@/lib/utils"
import { OvertimeStatus, UserRole } from "@/types/enums"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useAuth } from "@/contexts/auth"
import { useOvertime } from "@/contexts/overtime/useOvertime"
import { toast } from "sonner"
import { RequestDetailsModal } from "./RequestDetailsModal"
import { DataTable, DataTableColumn } from "@/components/ui/DataTable"
import { CheckIcon, XIcon, ShieldAlert, EyeIcon, User as UserIcon, Calendar, Clock, Building2, Search, RefreshCw, Download, ChevronDown } from "lucide-react"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem
} from "@/components/ui/dropdown-menu"
import { StatusBadge, getStatusColor } from "@/utils/statusUtils"


interface RequestTableProps {
  requests: OvertimeRequest[]
  title?: string
  showActions?: boolean
  isLoading?: boolean
  hideTitle?: boolean
  hideSubmittedByColumn?: boolean
  onActionComplete?: () => void
  config?: {
    showRefresh?: boolean
    showSearch?: boolean
    showExport?: boolean
    showColumnVisibility?: boolean
  }
  onRefresh?: () => void | Promise<void>
}

export function RequestTable({ 
  requests, 
  title = "Overtime Requests",
  showActions = false, 
  isLoading = false,
  hideTitle = false,
  hideSubmittedByColumn,
  onActionComplete,
  config,
  onRefresh
}: RequestTableProps) {
  const { user } = useAuth();
  const { approveOvertimeRequest, rejectOvertimeRequest, refreshRequests } = useOvertime();
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
  const [selectedRequest, setSelectedRequest] = useState<OvertimeRequest | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const handleRowClick = (request: OvertimeRequest) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  const handleApprove = async (id: string) => {
    try {
      setActionLoading(true);
      const success = await approveOvertimeRequest(id, "Approved");
      
      if (success && onActionComplete) {
        onActionComplete();
      }
    } catch (error) {
      toast.error('Failed to approve request');
      console.error(error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (id: string) => {
    try {
      setActionLoading(true);
      const success = await rejectOvertimeRequest(id, "Rejected");
      
      if (success && onActionComplete) {
        onActionComplete();
      }
    } catch (error) {
      toast.error('Failed to reject request');
      console.error(error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
  };

  // Enhanced helper to get employee initials
  const getInitials = (name: string): string => {
    if (!name) return '?';
    const names = name.trim().split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  // Define columns for the data table with UserTable styling
  const columns: DataTableColumn<OvertimeRequest>[] = [
    {
      key: 'employeeName',
      header: 'Employee',
      sortable: true,
      searchable: true,
      className: 'text-left',
      render: (value, request) => {
        const employeeName = request.employeeName || request.employee_name || `Employee ${request.employeeId?.substring(0, 4) || 'Unknown'}`;
        return (
          <div className="flex items-center space-x-3">
            <Avatar className="h-10 w-10 flex-shrink-0 ring-2 ring-background border-2 border-border">
              <AvatarFallback className="bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700 font-semibold">
                {getInitials(employeeName)}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col min-w-0">
              <span className="font-semibold text-foreground truncate">{employeeName}</span>
              <span className="text-sm text-muted-foreground truncate">
                ID: {String(request.employee_display_id || 'Unknown')}
              </span>
            </div>
          </div>
        );
      },
      exportRender: (value, request) => request.employeeName || request.employee_name || 'Unknown'
    },
    {
      key: 'department',
      header: 'Department',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center">
          <Badge variant="outline" className={`whitespace-normal ${getStatusColor('neutral', 'light')}`}>
            <Building2 className="h-3 w-3 mr-1" />
            {String(value)}
          </Badge>
        </div>
      ),
      exportRender: (value) => String(value)
    },
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center font-medium">
          <div className="flex items-center justify-center gap-1">
            <Calendar className="h-3 w-3 text-muted-foreground" />
            {formatDate(String(value))}
          </div>
        </div>
      ),
      exportRender: (value) => formatDate(String(value))
    },
    {
      key: 'hours',
      header: 'Hours',
      sortable: true,
      searchable: false,
      render: (value) => (
        <div className="text-center font-semibold">
          <div className="flex items-center justify-center gap-1">
            <Clock className="h-3 w-3 text-muted-foreground" />
            {formatHours(Number(value))}
          </div>
        </div>
      ),
      exportRender: (value) => formatHours(Number(value))
    }
  ];

  // Conditionally add Submitted By column
  if (!hideSubmittedByColumn && (user?.role === UserRole.PLANT_MANAGER || user?.role === UserRole.SUPER_ADMIN)) {
    columns.push({
      key: 'created_by_name',
      header: 'Submitted By',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center font-medium">
          <div className="flex items-center justify-center gap-1">
            <UserIcon className="h-3 w-3 text-muted-foreground" />
            {String(value || '-')}
          </div>
        </div>
      ),
      exportRender: (value) => String(value || '-')
    });
  }

  // Add Status column
  columns.push({
    key: 'status',
    header: 'Status',
    sortable: true,
    searchable: true,
    render: (value) => (
      <div className="text-center">
        <StatusBadge status={String(value)} className="px-3 py-1.5 font-medium" />
      </div>
    ),
    exportRender: (value) => String(value)
  });

  // Conditionally add VP Required column for super admin
  if (isSuperAdmin) {
    columns.push({
      key: 'vpApprovalRequired',
      header: 'Requires VP',
      sortable: true,
      searchable: false,
      render: (value, request) => (
        <div className="text-center">
                        {(request.vpApprovalRequired || request.status === OvertimeStatus.PENDING_VP_APPROVAL) && 
            <Badge variant="outline" className={getStatusColor('warning', 'light')}>
              <span className="flex items-center gap-1">
                <ShieldAlert className="h-3 w-3" />
                Required
              </span>
                          </Badge>
                        }
        </div>
      ),
      exportRender: (value, request) => (request.vpApprovalRequired || request.status === OvertimeStatus.PENDING_VP_APPROVAL) ? 'Required' : ''
    });
  }

  // Add Actions column if needed
  if (showActions) {
    columns.push({
      key: 'id',
      header: 'Actions',
      sortable: false,
      searchable: false,
      render: (value, request) => (
        <div className="text-center">
          <div className="flex items-center justify-center gap-2">
                          {(request.status === OvertimeStatus.PENDING || request.status === OvertimeStatus.PENDING_VP_APPROVAL || request.status === OvertimeStatus.PENDING_PM_APPROVAL || request.status === OvertimeStatus.PENDING_DH_APPROVAL) && (
                            <>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="destructive" 
                                      size="icon" 
                                      className="h-8 w-8 rounded-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReject(request.id);
                        }}
                                      disabled={actionLoading}
                                    >
                                      <XIcon className="h-4 w-4" />
                                      <span className="sr-only">Reject</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Reject request</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="secondary" 
                                      size="icon" 
                                      className="h-8 w-8 rounded-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(request.id);
                        }}
                                      disabled={actionLoading}
                                    >
                                      <CheckIcon className="h-4 w-4" />
                                      <span className="sr-only">Approve</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Approve request</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </>
                          )}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="icon" 
                                  className="h-8 w-8 rounded-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(request);
                    }}
                                >
                                  <EyeIcon className="h-4 w-4" />
                                  <span className="sr-only">View Details</span>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>View details</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
        </div>
      ),
      exportRender: () => 'Actions Available'
    });
  }

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardContent>
          <div className="py-8 text-center text-muted-foreground">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  if (!requests || requests.length === 0) {
    return (
      <Card className="w-full">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle className="text-lg font-semibold">{title}</CardTitle>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by employee, department, or status..."
                  disabled
                  className="pl-10 w-full sm:w-80"
                />
              </div>
              <div className="flex items-center gap-2">
                {config?.showRefresh && onRefresh && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onRefresh}
                    className="whitespace-nowrap"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                )}
                {config?.showExport && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled
                    className="whitespace-nowrap"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                )}
                {config?.showColumnVisibility && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        Columns <ChevronDown className="ml-2 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {columns.filter(column => !column.hideFromDropdown).map((column) => (
                        <DropdownMenuCheckboxItem
                          key={String(column.key)}
                          className="capitalize"
                          checked={true}
                          disabled
                        >
                          {typeof column.header === 'string' ? column.header : String(column.key)}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Empty State Message - Prominent at top */}
          <div className="py-12 text-center text-muted-foreground border-b border-gray-200 dark:border-gray-700 mb-6">
            <span className="text-2xl font-semibold block mb-3">No Pending Requests</span>
            <p className="text-base text-muted-foreground">When there are requests to approve, they will appear here</p>
          </div>
          
          <div className="space-y-3">
            {/* Table Header Skeleton */}
            <div className="flex items-center space-x-4 p-4 border-b">
              {columns.slice(0, 6).map((_, index) => (
                <div key={index} className="flex-1">
                  <Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
            
            {/* Table Rows Skeleton - Reduced to 3 rows for better visibility */}
            {Array.from({ length: 3 }).map((_, rowIndex) => (
              <div key={rowIndex} className="flex items-center space-x-4 p-4 border-b">
                {/* Employee Column */}
                <div className="flex items-center space-x-3 flex-1">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
                
                {/* Other Columns */}
                {columns.slice(1, 6).map((_, colIndex) => (
                  <div key={colIndex} className="flex-1">
                    <Skeleton className="h-4 w-16" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="w-full">
        <DataTable
          data={requests}
          columns={columns}
          title={hideTitle ? undefined : title}
          isLoading={isLoading}
          config={{
            searchPlaceholder: "Search by employee, department, or status...",
            defaultSortField: 'date',
            defaultSortDirection: 'desc',
            rowsPerPage: 10,
            showSearch: config?.showSearch ?? true,
            showColumnVisibility: config?.showColumnVisibility ?? true,
            showExport: config?.showExport ?? true,
            showRefresh: config?.showRefresh ?? true,
            emptyStateMessage: "No overtime requests found",
            noSearchResultsMessage: "No requests match your search criteria",
            exportFilename: 'overtime_requests'
          }}
          onRefresh={onRefresh || (async () => { await refreshRequests(); })}
        />
      </div>

      <RequestDetailsModal
        request={selectedRequest}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </>
  );
}

export default RequestTable;
